import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Avatar from '@mui/material/Avatar';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import TodayIcon from '@mui/icons-material/Today';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';
import TournamentTab from '../components/TournamentTab';
import SurvivalTab from '../components/SurvivalTab';
import RankingComparisonChart from '../components/RankingComparisonChart';
import { DayBadge, Last5DaysLegend } from '../components/Last5DaysBadges';
import apiService from '../services/ApiService';
import groupService from '../services/GroupService';
import shareService from '../services/ShareService';
import authService from '../services/AuthService';
import { debugError } from '../utils/debugUtils';

const RANKING_TYPES = [
  { labelKey: 'leaderboard.tabGlobal', value: 'ALL_TIME' },
  { labelKey: 'groups.rankingDaily', value: 'DAILY' },
  { labelKey: 'leaderboard.tabWeekly', value: 'WEEKLY' },
  { labelKey: 'leaderboard.tabMonthly', value: 'MONTHLY' },
  { labelKey: 'leaderboard.tabYearly', value: 'YEARLY' },
  { labelKey: 'leaderboard.tabComparison', value: 'COMPARISON' },
];

function initialsOf(name) {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

export default function GroupDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupId } = useParams();
  const currentUserId = authService.getUser()?.id;

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isAdmin = group?.myRole === 'ADMIN';

  const loadGroup = useCallback(() => {
    setLoading(true);
    setError(null);
    groupService
      .getDetail(groupId)
      .then(setGroup)
      .catch((err) => {
        debugError('[GroupDetailPage] Error loading group:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [groupId]);

  useEffect(() => {
    loadGroup();
  }, [loadGroup]);

  if (loading) return <LoadingSpinner />;

  if (error || !group) {
    return (
      <Box className="page-content" sx={{ gap: 2, maxWidth: 700, mx: 'auto', width: '100%' }}>
        <Typography textAlign="center" color="error" py={4}>
          {t('common.error')}
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/groups?view=all')} sx={{ alignSelf: 'center' }}>
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box className="page-content" sx={{ gap: 2, maxWidth: 700, mx: 'auto', width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={() => navigate('/groups?view=all')} sx={{ color: 'white' }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700} color="white" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }} noWrap>
            {group.name}
          </Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.85)">
            {t('groups.membersCount', { count: group.memberCount })}
          </Typography>
        </Box>
      </Box>

      <Card sx={{ width: '100%' }}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={t('groups.tabRanking')} />
            <Tab label={t('groups.tabMembers')} />
            <Tab label={t('groups.tabStats')} />
            <Tab label={t('groups.tabSettings')} />
          </Tabs>

          {tab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card variant="outlined">
                <CardContent>
                  <RankingTab groupId={groupId} t={t} />
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <TournamentTab groupId={groupId} isAdmin={isAdmin} t={t} onSnackbar={setSnackbar} />
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <SurvivalTab groupId={groupId} isAdmin={isAdmin} t={t} onSnackbar={setSnackbar} />
                </CardContent>
              </Card>
            </Box>
          )}
          {tab === 1 && (
            <MembersTab
              groupId={groupId}
              isAdmin={isAdmin}
              currentUserId={currentUserId}
              t={t}
              onSnackbar={setSnackbar}
            />
          )}
          {tab === 2 && <StatsTab groupId={groupId} t={t} />}
          {tab === 3 && (
            <SettingsTab
              group={group}
              isAdmin={isAdmin}
              t={t}
              onGroupUpdated={setGroup}
              onSnackbar={setSnackbar}
              onLeft={() => navigate('/groups', { replace: true })}
              onDeleted={() => navigate('/groups', { replace: true })}
            />
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function RankingTab({ groupId, t }) {
  const navigate = useNavigate();
  const [type, setType] = useState('ALL_TIME');
  const [topEntries, setTopEntries] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dailyTopic, setDailyTopic] = useState(null);

  const showLast5 = type === 'ALL_TIME';

  useEffect(() => {
    setLoading(true);
    setError(null);
    // Para la pestaña Comparativa reutilizamos el ranking ALL_TIME para preseleccionar el top de jugadores.
    groupService
      .getRanking(groupId, type === 'COMPARISON' ? 'ALL_TIME' : type)
      .then((data) => {
        setTopEntries(Array.isArray(data?.topEntries) ? data.topEntries : []);
        setCurrentUser(data?.currentUser ?? null);
      })
      .catch((err) => {
        debugError('[RankingTab] Error loading group ranking:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [groupId, type]);

  useEffect(() => {
    if (type !== 'DAILY') return;
    apiService
      .get('/api/v1/topics/previous')
      .then((data) => setDailyTopic(data || null))
      .catch((err) => {
        debugError('[RankingTab] Error loading daily topic:', err);
        setDailyTopic(null);
      });
  }, [type]);

  const renderLast5 = (p) => (
    <TableCell align="center">
      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
        {(p.last5Days || []).map((day, idx) => (
          <DayBadge
            key={idx}
            score={day.score}
            date={day.date}
            onClick={day.date ? () => navigate(`/history?date=${day.date}`) : undefined}
          />
        ))}
      </Box>
    </TableCell>
  );

  return (
    <Box>
      <Tabs
        value={RANKING_TYPES.findIndex((r) => r.value === type)}
        onChange={(_, idx) => setType(RANKING_TYPES[idx].value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {RANKING_TYPES.map((r) => (
          <Tab key={r.value} label={t(r.labelKey)} />
        ))}
      </Tabs>

      {type === 'DAILY' && dailyTopic && (
        <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <TodayIcon color="primary" fontSize="small" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {dailyTopic.topic}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(dailyTopic.date + 'T00:00:00').toLocaleDateString(undefined, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {type === 'COMPARISON' ? (
        loading ? (
          <LoadingSpinner />
        ) : (
          <RankingComparisonChart groupId={groupId} initialTopEntries={topEntries} t={t} />
        )
      ) : loading ? (
        <LoadingSpinner />
      ) : error ? (
        <Typography textAlign="center" color="error" py={2}>
          {t('common.error')}
        </Typography>
      ) : topEntries.length === 0 ? (
        <Typography textAlign="center" color="text.secondary" py={4}>
          {t('groups.noRanking')}
        </Typography>
      ) : (
        <>
          {showLast5 && <Last5DaysLegend t={t} />}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 48 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('leaderboard.player')}</TableCell>
                  {showLast5 && (
                    <TableCell sx={{ fontWeight: 700 }} align="center">
                      {t('leaderboard.last5Days')}
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700 }} align="right">{t('leaderboard.total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topEntries.map((p) => (
                  <TableRow key={p.rank}>
                    <TableCell>{p.rank}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    {showLast5 && renderLast5(p)}
                    <TableCell align="right">
                      <Typography fontWeight={700} color="primary.main">{p.totalScore}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {currentUser && (
                  <TableRow>
                    <TableCell>{currentUser.rank}</TableCell>
                    <TableCell>{currentUser.name}</TableCell>
                    {showLast5 && renderLast5(currentUser)}
                    <TableCell align="right">
                      <Typography fontWeight={700}>{currentUser.totalScore}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

function MembersTab({ groupId, isAdmin, currentUserId, t, onSnackbar }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kickTarget, setKickTarget] = useState(null);
  const [kicking, setKicking] = useState(false);

  const loadMembers = useCallback(() => {
    setLoading(true);
    groupService
      .listMembers(groupId)
      .then((data) => setMembers(Array.isArray(data) ? data : []))
      .catch((err) => debugError('[MembersTab] Error loading members:', err))
      .finally(() => setLoading(false));
  }, [groupId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleKick = async () => {
    if (!kickTarget) return;
    setKicking(true);
    try {
      await groupService.kickMember(groupId, kickTarget.userId);
      setMembers((prev) => prev.filter((m) => m.userId !== kickTarget.userId));
      setKickTarget(null);
    } catch (err) {
      onSnackbar({ open: true, message: t(`apiErrors.${err.code}`, t('common.error')), severity: 'error' });
    } finally {
      setKicking(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {members.map((member) => (
          <Box key={member.userId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.9rem' }}>
              {initialsOf(member.name)}
            </Avatar>
            <Typography sx={{ flex: 1 }} noWrap>{member.name}</Typography>
            <Chip
              size="small"
              label={t(member.role === 'ADMIN' ? 'groups.roleAdmin' : 'groups.roleMember')}
              color={member.role === 'ADMIN' ? 'primary' : 'default'}
            />
            {isAdmin && member.userId !== currentUserId && (
              <IconButton size="small" color="error" onClick={() => setKickTarget(member)}>
                <PersonRemoveIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>

      <ConfirmDialog
        open={!!kickTarget}
        title={t('groups.kickConfirmTitle')}
        message={t('groups.kickConfirmMessage', { name: kickTarget?.name })}
        confirmLabel={t('groups.kickButton')}
        loading={kicking}
        onConfirm={handleKick}
        onClose={() => setKickTarget(null)}
      />
    </>
  );
}

function StatsTab({ groupId, t }) {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    groupService
      .getStats(groupId)
      .then((data) => setStats(Array.isArray(data) ? data : []))
      .catch((err) => debugError('[StatsTab] Error loading stats:', err))
      .finally(() => setLoading(false));
  }, [groupId]);

  if (loading) return <LoadingSpinner />;

  if (stats.length === 0) {
    return (
      <Typography textAlign="center" color="text.secondary" py={4}>
        {t('groups.noStats')}
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>{t('leaderboard.player')}</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">{t('groups.statsGamesPlayed')}</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">{t('groups.statsWins')}</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">{t('groups.statsCurrentStreak')}</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">{t('groups.statsBestStreak')}</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">{t('groups.statsTotalScore')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stats.map((s) => (
            <TableRow key={s.userId}>
              <TableCell>{s.name}</TableCell>
              <TableCell align="right">{s.gamesPlayed}</TableCell>
              <TableCell align="right">{s.wins}</TableCell>
              <TableCell align="right">{s.currentStreak}</TableCell>
              <TableCell align="right">{s.bestStreak}</TableCell>
              <TableCell align="right">
                <Typography fontWeight={700} color="primary.main">{s.totalScore}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const SCORING_MODES = ['SEPARATE', 'GLOBAL'];

function scoringModeLabelKey(mode) {
  return mode === 'SEPARATE' ? 'groups.scoringModeSeparate' : 'groups.scoringModeGlobal';
}

function ScoringModeSection({ group, isAdmin, t, onGroupUpdated, onSnackbar }) {
  const [savingMode, setSavingMode] = useState(false);

  const handleModeChange = async (mode) => {
    if (mode === group.scoringMode || savingMode) return;
    setSavingMode(true);
    try {
      const updated = await groupService.updateScoringMode(group.id, mode);
      onGroupUpdated(updated);
      onSnackbar({ open: true, message: t('common.save'), severity: 'success' });
    } catch (err) {
      onSnackbar({ open: true, message: t(`apiErrors.${err.code}`, t('common.error')), severity: 'error' });
    } finally {
      setSavingMode(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {t('groups.scoringModeLabel')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('groups.scoringModeHelp')}
      </Typography>
      {isAdmin ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {SCORING_MODES.map((mode) => (
            <Button
              key={mode}
              variant={group.scoringMode === mode ? 'contained' : 'outlined'}
              size="small"
              disabled={savingMode}
              onClick={() => handleModeChange(mode)}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {t(scoringModeLabelKey(mode))}
            </Button>
          ))}
        </Box>
      ) : (
        <Chip size="small" color="primary" label={t(scoringModeLabelKey(group.scoringMode))} />
      )}
    </Box>
  );
}

function SettingsTab({ group, isAdmin, t, onGroupUpdated, onSnackbar, onLeft, onDeleted }) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description || '');
  const [saving, setSaving] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await groupService.update(group.id, name.trim(), description.trim() || null);
      onGroupUpdated(updated);
      onSnackbar({ open: true, message: t('common.save'), severity: 'success' });
    } catch (err) {
      onSnackbar({ open: true, message: t(`apiErrors.${err.code}`, t('common.error')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyCode = async () => {
    await shareService.copyToClipboard(group.inviteCode);
    onSnackbar({ open: true, message: t('groups.copied'), severity: 'success' });
  };

  const handleShareInvite = async () => {
    const text = t('groups.shareMessage', { code: group.inviteCode });
    try {
      if (Capacitor.isNativePlatform()) {
        await Share.share({ title: 'Concordare', text });
      } else if (navigator.share) {
        await navigator.share({ title: 'Concordare', text });
      } else {
        await shareService.copyToClipboard(text);
        onSnackbar({ open: true, message: t('groups.copied'), severity: 'success' });
      }
    } catch {
      // usuario canceló el share nativo, ignorar
    }
  };

  const handleLeave = async () => {
    setLeaving(true);
    try {
      await groupService.leave(group.id);
      onLeft();
    } catch (err) {
      onSnackbar({ open: true, message: t(`apiErrors.${err.code}`, t('common.error')), severity: 'error' });
      setLeaveOpen(false);
    } finally {
      setLeaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await groupService.remove(group.id);
      onDeleted();
    } catch (err) {
      onSnackbar({ open: true, message: t(`apiErrors.${err.code}`, t('common.error')), severity: 'error' });
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {t('groups.tabSettings')}
        </Typography>
        <TextField
          label={t('groups.namePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!isAdmin}
          inputProps={{ maxLength: 50 }}
          fullWidth
          size="small"
          sx={{ mb: 1.5 }}
        />
        <TextField
          label={t('groups.descriptionPlaceholder')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={!isAdmin}
          inputProps={{ maxLength: 255 }}
          multiline
          minRows={2}
          fullWidth
          size="small"
        />
        {isAdmin && (
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ mt: 1.5 }}>
            {t('common.save')}
          </Button>
        )}
      </Box>

      <Divider />

      <ScoringModeSection group={group} isAdmin={isAdmin} t={t} onGroupUpdated={onGroupUpdated} onSnackbar={onSnackbar} />

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {t('groups.inviteTitle')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField value={group.inviteCode || ''} size="small" InputProps={{ readOnly: true }} fullWidth />
          <IconButton onClick={handleCopyCode} aria-label={t('groups.copyCode')}>
            <ContentCopyIcon />
          </IconButton>
          <IconButton onClick={handleShareInvite} aria-label={t('groups.shareInvite')}>
            <ShareIcon />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button variant="outlined" color="error" onClick={() => setLeaveOpen(true)}>
          {t('groups.leaveButton')}
        </Button>
        {isAdmin && (
          <Button variant="outlined" color="error" onClick={() => setDeleteOpen(true)}>
            {t('groups.deleteButton')}
          </Button>
        )}
      </Box>

      <ConfirmDialog
        open={leaveOpen}
        title={t('groups.leaveConfirmTitle')}
        message={t('groups.leaveConfirmMessage')}
        confirmLabel={t('groups.leaveButton')}
        loading={leaving}
        onConfirm={handleLeave}
        onClose={() => setLeaveOpen(false)}
      />
      <ConfirmDialog
        open={deleteOpen}
        title={t('groups.deleteConfirmTitle')}
        message={t('groups.deleteConfirmMessage')}
        confirmLabel={t('groups.deleteButton')}
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteOpen(false)}
      />
    </Box>
  );
}
